import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Users, Baby, BedDouble, ChevronDown, User, UserRound, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { RoomGuest } from "./MultiroomGuestPicker";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface GuestPickerProps {
  guests: { adults: number; children: number; rooms: number; childAges?: number[]; roomsConfig?: RoomGuest[] };
  onChange: (guests: { adults: number; children: number; rooms: number; childAges?: number[]; roomsConfig?: RoomGuest[] }) => void;
  onClose: () => void;
}

export const GuestPicker = ({ guests, onChange, onClose }: GuestPickerProps) => {
  const [childAges, setChildAges] = useState<number[]>(
    guests.childAges || (guests.children > 0 ? Array(guests.children).fill(5) : [])
  );
  const [isRoomDistributionExpanded, setIsRoomDistributionExpanded] = useState(false);
  // REMOVED: Local roomsConfig state - using guests.roomsConfig as single source of truth
  const [activeRoomTab, setActiveRoomTab] = useState(0);
  const [isChildAgesExpanded, setIsChildAgesExpanded] = useState(true);
  const [isManuallyCustomized, setIsManuallyCustomized] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  
  // Computed value: use guests.roomsConfig directly from props
  const roomsConfig = guests.roomsConfig || [];

  // Sync childAges with prop changes
  useEffect(() => {
    if (guests.childAges && JSON.stringify(guests.childAges) !== JSON.stringify(childAges)) {
      setChildAges(guests.childAges);
    }
  }, [guests.childAges]);


  // Smooth scroll to active tab
  const scrollToActiveTab = (tabIndex: number) => {
    if (tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const activeTab = container.children[tabIndex] as HTMLElement;
      
      if (activeTab) {
        const containerWidth = container.offsetWidth;
        const tabLeft = activeTab.offsetLeft;
        const tabWidth = activeTab.offsetWidth;
        const scrollPosition = tabLeft - (containerWidth / 2) + (tabWidth / 2);
        
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  // Auto-scroll when active tab changes
  useEffect(() => {
    scrollToActiveTab(activeRoomTab);
  }, [activeRoomTab]);

  // Initialize rooms config if switching to multiroom mode - ensures minimum 1 adult per room
  // and respects ETG per-room limits: max 6 adults, max 4 children per room
  const initializeRoomsConfig = (): RoomGuest[] => {
    // If roomsConfig exists but count doesn't match, we need to rebuild
    if (guests.roomsConfig && guests.roomsConfig.length === guests.rooms) {
      return guests.roomsConfig;
    }
    
    const MAX_ADULTS_PER_ROOM = 6;
    const MAX_CHILDREN_PER_ROOM = 4;
    
    // Smart auto-distribute guests across rooms - ensuring minimum 1 adult per room
    // and respecting per-room limits
    
    // Ensure at least 1 adult per room
    const adultsPerRoom = Array(guests.rooms).fill(1); // Start with 1 adult each
    let remainingAdults = guests.adults - guests.rooms; // Extra adults to distribute
    
    // Distribute remaining adults in round-robin while respecting max per room
    let roomIndex = 0;
    while (remainingAdults > 0) {
      if (adultsPerRoom[roomIndex] < MAX_ADULTS_PER_ROOM) {
        adultsPerRoom[roomIndex]++;
        remainingAdults--;
      }
      roomIndex = (roomIndex + 1) % guests.rooms;
      
      // Safety: if all rooms are at max, stop
      if (adultsPerRoom.every(a => a >= MAX_ADULTS_PER_ROOM)) break;
    }
    
    // Round-robin distribution for children while respecting max per room
    const childrenDistribution: number[] = Array(guests.rooms).fill(0);
    let childRoomIndex = 0;
    for (let i = 0; i < guests.children; i++) {
      // Find next room that can accept a child
      let attempts = 0;
      while (childrenDistribution[childRoomIndex] >= MAX_CHILDREN_PER_ROOM && attempts < guests.rooms) {
        childRoomIndex = (childRoomIndex + 1) % guests.rooms;
        attempts++;
      }
      if (childrenDistribution[childRoomIndex] < MAX_CHILDREN_PER_ROOM) {
        childrenDistribution[childRoomIndex]++;
      }
      childRoomIndex = (childRoomIndex + 1) % guests.rooms;
    }
    
    let remainingChildren = [...childAges];
    const config: RoomGuest[] = [];
    
    for (let i = 0; i < guests.rooms; i++) {
      const roomChildrenCount = childrenDistribution[i];
      const roomChildren = remainingChildren.splice(0, roomChildrenCount);
      
      config.push({
        adults: adultsPerRoom[i],
        children: roomChildren
      });
    }
    
    return config;
  };

  // Auto-initialize room distribution when rooms > 1
  useEffect(() => {
    if (guests.rooms > 1 && !guests.roomsConfig) {
      const config = initializeRoomsConfig();
      onChange({ ...guests, roomsConfig: config });
      
      // Auto-collapse child ages section when switching to multiroom
      if (guests.children > 0) {
        setIsChildAgesExpanded(false);
      }
      
      // Scroll to last tab when adding rooms
      setTimeout(() => scrollToActiveTab(guests.rooms - 1), 100);
    } else if (guests.rooms === 1 && guests.roomsConfig) {
      const { roomsConfig: _, ...guestsWithoutConfig } = guests;
      onChange(guestsWithoutConfig);
    }
  }, [guests.rooms]);

  // REMOVED: Auto-sync useEffect that was interfering with manual room distribution
  // Manual distribution is now controlled exclusively through updateRoomGuest()

  const updateRoomGuest = (roomIndex: number, field: 'adults' | 'children', value: number) => {
    // Validate room index
    if (roomIndex < 0 || roomIndex >= roomsConfig.length) {
      console.error(`❌ Invalid room index: ${roomIndex}, roomsConfig length: ${roomsConfig.length}`);
      toast.error('Invalid room selection');
      return;
    }
    
    // Enforce minimum 1 adult per room
    if (field === 'adults' && value < 1) {
      toast.error("Each room must have at least 1 adult");
      return;
    }
    
    // Mark as manually customized when user edits per-room child distribution
    if (field === 'children') {
      setIsManuallyCustomized(true);
    }
    
    console.log(`🔧 updateRoomGuest called:`, { roomIndex, field, value, activeRoomTab, currentConfigLength: roomsConfig.length });
    console.log(`📊 Current config from props:`, JSON.parse(JSON.stringify(roomsConfig)));
    
    // Calculate allocations from current config
    const otherRoomsAdults = roomsConfig.reduce((sum, room, idx) => 
      idx === roomIndex ? sum : sum + room.adults, 0
    );
    const otherRoomsChildren = roomsConfig.reduce((sum, room, idx) => 
      idx === roomIndex ? sum : sum + room.children.length, 0
    );
    
    // Maximum this room can have
    const maxAdults = guests.adults - otherRoomsAdults;
    const maxChildren = guests.children - otherRoomsChildren;
    
    // Validate before updating
    if (field === 'adults' && value > maxAdults) {
      toast.error(`Cannot allocate more than ${maxAdults} adults to this room`);
      return;
    }
    
    if (field === 'children' && value > maxChildren) {
      toast.error(`Cannot allocate more than ${maxChildren} children to this room`);
      return;
    }
    
    // Create new config with the update - directly from current config
    const newConfig = roomsConfig.map((room, idx) => {
      if (idx !== roomIndex) return room;
      
      if (field === 'adults') {
        return { ...room, adults: value };
      } else {
        // Adjust children array
        const currentChildren = room.children.length;
        if (value > currentChildren) {
          return {
            ...room,
            children: [...room.children, ...Array(value - currentChildren).fill(5)]
          };
        } else {
          return {
            ...room,
            children: room.children.slice(0, value)
          };
        }
      }
    });
    
    console.log(`✅ New config:`, JSON.parse(JSON.stringify(newConfig)));
    
    // Update parent immediately with new config - NO LOCAL STATE UPDATE
    const allChildAges = newConfig.flatMap(room => room.children);
    onChange({
      adults: guests.adults,
      children: guests.children,
      rooms: guests.rooms,
      childAges: allChildAges,
      roomsConfig: newConfig
    });
  };

  const updateRoomChildAge = (roomIndex: number, childIndex: number, age: number) => {
    const newConfig = [...roomsConfig];
    newConfig[roomIndex].children[childIndex] = age;
    
    const allChildAges = newConfig.flatMap(room => room.children);
    onChange({ ...guests, childAges: allChildAges, roomsConfig: newConfig });
  };

  const updateGuest = (field: 'adults' | 'children' | 'rooms', increment: boolean) => {
    const value = guests[field] as number;
    const newValue = increment ? value + 1 : value - 1;
    
    // Validation with user feedback
    if (field === "adults" && newValue < 1) return;
    if (field === "children" && newValue < 0) return;
    if (field === "rooms" && newValue < 1) return;
    
    // ETG Limits: 6 adults and 4 children PER ROOM
    // For single room: enforce limits directly
    // For multi-room: each room can have up to 6 adults + 4 children
    const isMultiRoom = guests.rooms > 1;
    
    if (field === "rooms" && newValue > 5) {
      toast.warning("Maximum 5 rooms allowed per booking");
      return;
    }
    
    // Ensure enough adults for rooms (minimum 1 adult per room)
    if (field === "rooms" && increment && guests.adults < newValue) {
      toast.warning(`Need at least ${newValue} adults to book ${newValue} rooms (1 adult per room minimum)`);
      return;
    }
    
    if (!isMultiRoom) {
      // Single room: enforce 6 adults, 4 children, 10 total per room
      if (field === "adults" && newValue > 6) {
        toast.warning("Maximum 6 adults per room");
        return;
      }
      if (field === "children" && newValue > 4) {
        toast.warning("Maximum 4 children per room");
        return;
      }
      const currentAdults = field === "adults" ? newValue : guests.adults;
      const currentChildren = field === "children" ? newValue : guests.children;
      if (currentAdults + currentChildren > 10) {
        toast.warning("Maximum 10 total guests per room");
        return;
      }
    } else {
      // Multi-room: allow up to 6 adults * rooms and 4 children * rooms
      const maxAdults = 6 * guests.rooms;
      const maxChildren = 4 * guests.rooms;
      
      if (field === "adults" && newValue > maxAdults) {
        toast.warning(`Maximum ${maxAdults} adults for ${guests.rooms} rooms (6 per room)`);
        return;
      }
      if (field === "children" && newValue > maxChildren) {
        toast.warning(`Maximum ${maxChildren} children for ${guests.rooms} rooms (4 per room)`);
        return;
      }
    }

    // Handle child ages when children count changes
    if (field === "children") {
      if (increment) {
        const newAges = [...childAges, 5];
        setChildAges(newAges);
        
        // If multi-room: auto-distribute evenly if not manually customized, otherwise add to first room with space
        if (guests.rooms > 1 && roomsConfig.length > 0) {
          const newConfig = [...roomsConfig];
          
          if (!isManuallyCustomized) {
            // Find room with fewest children to maintain even distribution
            let minChildren = Infinity;
            let targetRoomIndex = 0;
            
            for (let i = 0; i < newConfig.length; i++) {
              if (newConfig[i].children.length < minChildren) {
                minChildren = newConfig[i].children.length;
                targetRoomIndex = i;
              }
            }
            
            newConfig[targetRoomIndex].children.push(5);
          } else {
            // User has manually customized - just add to first room with space
            let addedToRoom = false;
            for (let i = 0; i < newConfig.length; i++) {
              const roomTotal = newConfig[i].adults + newConfig[i].children.length;
              if (roomTotal < 10 && newConfig[i].children.length < 4) { // Max 10 guests per room, max 4 children
                newConfig[i].children.push(5);
                addedToRoom = true;
                break;
              }
            }
            if (!addedToRoom) {
              newConfig[0].children.push(5);
            }
          }
          
          onChange({ ...guests, children: newValue, childAges: newAges, roomsConfig: newConfig });
        } else {
          onChange({ ...guests, children: newValue, childAges: newAges });
        }
      } else {
        const newAges = childAges.slice(0, newValue);
        setChildAges(newAges);
        
        // If multi-room: remove child from last room that has children (respects manual customization)
        if (guests.rooms > 1 && roomsConfig.length > 0) {
          const newConfig = [...roomsConfig];
          
          if (!isManuallyCustomized) {
            // Find room with most children to maintain even distribution
            let maxChildren = -1;
            let targetRoomIndex = 0;
            
            for (let i = 0; i < newConfig.length; i++) {
              if (newConfig[i].children.length > maxChildren) {
                maxChildren = newConfig[i].children.length;
                targetRoomIndex = i;
              }
            }
            
            if (newConfig[targetRoomIndex].children.length > 0) {
              newConfig[targetRoomIndex].children.pop();
            }
          } else {
            // User has manually customized - remove from last room with children
            for (let i = newConfig.length - 1; i >= 0; i--) {
              if (newConfig[i].children.length > 0) {
                newConfig[i].children.pop();
                break;
              }
            }
          }
          
          onChange({ ...guests, children: newValue, childAges: newAges, roomsConfig: newConfig });
        } else {
          onChange({ ...guests, children: newValue, childAges: newAges });
        }
      }
      return;
    } else if (field === "adults") {
      // If multi-room: add/remove adult from appropriate room
      if (guests.rooms > 1 && roomsConfig.length > 0) {
        const newConfig = [...roomsConfig];
        if (increment) {
          // Add adult to first room with space
          let addedToRoom = false;
          for (let i = 0; i < newConfig.length; i++) {
            const roomTotal = newConfig[i].adults + newConfig[i].children.length;
            if (roomTotal < 10 && newConfig[i].adults < 6) { // Max 10 guests per room, max 6 adults
              newConfig[i].adults++;
              addedToRoom = true;
              break;
            }
          }
          if (!addedToRoom) {
            newConfig[0].adults++;
          }
        } else {
          // Remove adult from last room that has MORE than 1 adult (keep minimum 1)
          for (let i = newConfig.length - 1; i >= 0; i--) {
            if (newConfig[i].adults > 1) {
              newConfig[i].adults--;
              break;
            }
          }
        }
        onChange({ ...guests, adults: newValue, childAges, roomsConfig: newConfig });
      } else {
        onChange({ ...guests, adults: newValue, childAges });
      }
    } else {
      onChange({ ...guests, [field]: newValue, childAges });
    }
  };

  const updateChildAge = (index: number, age: number) => {
    const newAges = [...childAges];
    newAges[index] = age;
    setChildAges(newAges);
    onChange({ 
      adults: guests.adults, 
      children: guests.children, 
      rooms: guests.rooms, 
      childAges: newAges 
    });
  };

  const handleButtonClick = (e: React.MouseEvent, field: 'adults' | 'children' | 'rooms', increment: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    updateGuest(field, increment);
  };


  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="space-y-3.5"
    >
      {/* Adults Counter */}
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-3">
          <User className="w-5 h-5 text-[#eac177] opacity-90" />
          <div>
            <p className="font-semibold text-white/92 text-[16px]">Adults</p>
            <p className="text-[13.5px] text-white/55">Ages 18+</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'adults', false)}
            disabled={guests.adults <= 1}
            className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/[0.08] hover:border-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Minus className="h-3.5 w-3.5 text-white/90" />
          </button>
          <span className="min-w-[2.5rem] text-center font-semibold text-white text-[16px]">
            {guests.adults}
          </span>
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'adults', true)}
            disabled={guests.rooms > 1 ? guests.adults >= 6 * guests.rooms : guests.adults >= 6}
            className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/[0.08] hover:border-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Plus className="h-3.5 w-3.5 text-white/90" />
          </button>
        </div>
      </div>

      {/* Children Counter */}
      <div className="flex items-center justify-between py-3 border-t border-white/[0.07]">
        <div className="flex items-center gap-3">
          <Baby className="w-5 h-5 text-[#eac177] opacity-90" />
          <div>
            <p className="font-semibold text-white/92 text-[16px]">Children</p>
            <p className="text-[13.5px] text-white/55">Ages 0-17</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'children', false)}
            disabled={guests.children <= 0}
            className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/[0.08] hover:border-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Minus className="h-3.5 w-3.5 text-white/90" />
          </button>
          <span className="min-w-[2.5rem] text-center font-semibold text-white text-[16px]">
            {guests.children}
          </span>
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'children', true)}
            disabled={guests.rooms > 1 ? guests.children >= 4 * guests.rooms : guests.children >= 4}
            className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/[0.08] hover:border-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Plus className="h-3.5 w-3.5 text-white/90" />
          </button>
        </div>
      </div>

      {/* Child Ages */}
      <AnimatePresence>
        {guests.children > 0 && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="pt-3 border-t border-white/[0.07]"
          >
            <Collapsible open={isChildAgesExpanded} onOpenChange={setIsChildAgesExpanded}>
              <div className="space-y-3">
                {/* Collapsed Summary */}
                {!isChildAgesExpanded && (
                  <CollapsibleTrigger asChild>
                    <button className="w-full flex items-center justify-between group">
                      <div className="flex flex-col items-start gap-1">
                        <div className="text-[13.5px] font-medium text-white/55">
                          Child ages
                        </div>
                        <div className="text-[14px] text-white/92 mt-0.5">
                          {childAges.slice(0, guests.children).map((age, idx) => (
                            <span key={idx}>
                              {age}y
                              {idx < guests.children - 1 && <span className="text-[#EAC177] mx-2">•</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronDown className="w-4 h-4 text-[#eac177] group-hover:text-[#eac177]/80 transition-colors" />
                    </button>
                  </CollapsibleTrigger>
                )}
                
                {/* Expanded Dropdowns */}
                <CollapsibleContent>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-2"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[13.5px] font-medium text-white/55">
                        Child ages
                      </div>
                      <button
                        onClick={() => setIsChildAgesExpanded(false)}
                        className="p-1 hover:bg-white/[0.08] rounded transition-colors"
                      >
                        <ChevronDown className="w-4 h-4 text-[#eac177] rotate-180" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {childAges.slice(0, guests.children).map((age, index) => (
                        <div key={index} className="space-y-1">
                          <span className="text-[11px] text-white/35">Child {index + 1}</span>
                          <select
                            value={age}
                            onChange={(e) => updateChildAge(index, parseInt(e.target.value))}
                            className="w-full px-3 py-[6px] h-[34px] bg-[rgba(0,0,0,0.45)] border border-[rgba(234,193,119,0.4)] rounded-lg text-[13px] font-medium text-white/92 appearance-none cursor-pointer hover:border-[#eac177] focus:border-[#eac177] focus:ring-2 focus:ring-[rgba(234,193,119,0.12)] transition-all shadow-[0_0_12px_rgba(234,193,119,0.12)]"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23eac177' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1em 1em',
                              paddingRight: '2rem',
                            }}
                          >
                            {Array.from({ length: 18 }, (_, i) => (
                              <option key={i} value={i} className="bg-[rgba(0,0,0,0.95)] text-white">
                                {i} {i === 1 ? 'year' : 'years'}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rooms Counter */}
      <div className="pt-3 border-t border-white/[0.07]">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <BedDouble className="w-5 h-5 text-[#eac177] opacity-90" />
            <div>
              <div className="font-semibold text-white/92 text-[16px]">Rooms</div>
              <div className="text-[13.5px] text-white/55 mt-0.5">Max 5 rooms</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={(e) => handleButtonClick(e, "rooms", false)}
              disabled={guests.rooms <= 1}
              className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/[0.08] hover:border-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Minus className="h-3.5 w-3.5 text-white/90" />
            </button>
            <span className="min-w-[2.5rem] text-center font-semibold text-white text-[16px]">
              {guests.rooms}
            </span>
            <button
              type="button"
              onClick={(e) => handleButtonClick(e, "rooms", true)}
              disabled={guests.rooms >= 5}
              className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/[0.08] hover:border-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Plus className="h-3.5 w-3.5 text-white/90" />
            </button>
          </div>
        </div>
      </div>
      {/* SECTION 2: Room Distribution - Collapsible (Only when rooms > 1) */}
      <AnimatePresence>
        {guests.rooms > 1 && roomsConfig.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="pt-4 border-t border-white/[0.07]"
          >
            <Collapsible open={isRoomDistributionExpanded} onOpenChange={setIsRoomDistributionExpanded}>
              <div className="w-full">
                {/* Calculate unassigned guests for warning */}
                {(() => {
                  const assignedAdults = roomsConfig.reduce((sum, room) => sum + room.adults, 0);
                  const assignedChildren = roomsConfig.reduce((sum, room) => sum + room.children.length, 0);
                  const unassignedAdults = guests.adults - assignedAdults;
                  const unassignedChildren = guests.children - assignedChildren;
                  const hasUnassigned = unassignedAdults > 0 || unassignedChildren > 0;
                  
                  return (
                    <>
                      {/* Warning Banner - Always visible when guests unassigned */}
                      {hasUnassigned && !isRoomDistributionExpanded && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mb-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30"
                        >
                          <p className="text-[13px] text-amber-400 flex items-center gap-2">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span>
                              <span className="font-medium">
                                {unassignedAdults > 0 && `${unassignedAdults} adult${unassignedAdults !== 1 ? 's' : ''}`}
                                {unassignedAdults > 0 && unassignedChildren > 0 && ' and '}
                                {unassignedChildren > 0 && `${unassignedChildren} child${unassignedChildren !== 1 ? 'ren' : ''}`}
                              </span>
                              {' not assigned to rooms'}
                            </span>
                          </p>
                        </motion.div>
                      )}
                      
                      {/* Collapsed Preview */}
                      <CollapsibleTrigger asChild>
                        <button className="w-full flex items-center justify-between group py-2">
                          <div 
                            className="flex flex-col items-start gap-2.5 cursor-pointer"
                            onClick={() => {
                              setIsRoomDistributionExpanded(true);
                              setTimeout(() => scrollToActiveTab(0), 100);
                            }}
                          >
                            <span className="text-[16px] font-semibold text-white/92">Room Distribution</span>
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(234,193,119,0.2)]">
                                <BedDouble className="w-3.5 h-3.5 text-[#EAC177]" />
                                <span className="text-[14px] text-white/92 font-medium whitespace-nowrap">
                                  {guests.rooms} Room{guests.rooms !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(234,193,119,0.2)]">
                                <User className="w-3.5 h-3.5 text-[#EAC177]" />
                                <span className="text-[14px] text-white/92 font-medium whitespace-nowrap">
                                  {guests.adults} Adult{guests.adults !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <AnimatePresence mode="wait">
                                {guests.children > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.2, ease: "easeOut" }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(234,193,119,0.2)]"
                                  >
                                    <Baby className="w-3.5 h-3.5 text-[#EAC177]" />
                                    <span className="text-[14px] text-white/92 font-medium whitespace-nowrap">
                                      {guests.children} Child{guests.children !== 1 ? 'ren' : ''}
                                    </span>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            <span className={`text-[13.5px] font-medium transition-colors ${hasUnassigned ? 'text-amber-400 group-hover:text-amber-300' : 'text-[#eac177] group-hover:text-[#eac177]/80'}`}>
                              {isRoomDistributionExpanded ? 'Hide' : hasUnassigned ? 'Assign' : 'Edit'}
                            </span>
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${hasUnassigned ? 'text-amber-400' : 'text-[#eac177]'} ${isRoomDistributionExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                </CollapsibleTrigger>

                {/* Expanded Tabbed Room Configuration */}
                <CollapsibleContent>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="mt-4"
                  >
                    {/* Tab Navigation - Scrollable */}
                    <div className="relative">
                      <div 
                        ref={tabsContainerRef}
                        className="flex gap-1 mb-4 border-b border-white/[0.08] overflow-x-auto scrollbar-none max-w-full scroll-smooth"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      >
                        {roomsConfig.map((room, roomIndex) => (
                          <button
                            key={roomIndex}
                            onClick={() => {
                              setActiveRoomTab(roomIndex);
                              scrollToActiveTab(roomIndex);
                            }}
                            className={`flex-shrink-0 px-4 py-2.5 text-[15px] font-semibold transition-all relative ${
                              activeRoomTab === roomIndex
                                ? 'text-[#EAC177]'
                                : 'text-[rgba(255,255,255,0.45)] hover:text-[rgba(255,255,255,0.65)]'
                            }`}
                          >
                            <div className="flex flex-col items-center gap-0.5">
                              <span>Room {roomIndex + 1}</span>
                              <span className="text-[11px] text-white/35 font-normal whitespace-nowrap">
                                {room.adults} adult{room.adults !== 1 ? 's' : ''}{room.children.length > 0 && ` · ${room.children.length} child${room.children.length !== 1 ? 'ren' : ''}`}
                              </span>
                            </div>
                            {activeRoomTab === roomIndex && (
                              <motion.div
                                layoutId="activeTab"
                                className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#EAC177] shadow-[0_0_8px_rgba(234,193,119,0.4)]"
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Active Room Content */}
                    <AnimatePresence mode="wait">
                      {(() => {
                        // Calculate how many adults/children are UNALLOCATED in OTHER rooms
                        const otherRoomsAdults = roomsConfig.reduce((sum, room, idx) => 
                          idx === activeRoomTab ? sum : sum + room.adults, 0
                        );
                        const otherRoomsChildren = roomsConfig.reduce((sum, room, idx) => 
                          idx === activeRoomTab ? sum : sum + room.children.length, 0
                        );
                        
                        // Maximum this room can have = Global total - Other rooms' allocations
                        const maxAdultsForThisRoom = guests.adults - otherRoomsAdults;
                        const maxChildrenForThisRoom = guests.children - otherRoomsChildren;
                        
                        return (
                          <motion.div
                            key={`room-${activeRoomTab}-adults-${roomsConfig[activeRoomTab]?.adults || 0}-children-${roomsConfig[activeRoomTab]?.children.length || 0}`}
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                            className="p-4 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(234,193,119,0.30)] shadow-[0_0_18px_rgba(234,193,119,0.12)]"
                          >
                            {/* Visual Feedback - Available Guests */}
                            <div className="mb-3 p-2.5 rounded-lg bg-white/[0.03] border border-[#EAC177]/20">
                              {(() => {
                                const availableAdults = maxAdultsForThisRoom - roomsConfig[activeRoomTab].adults;
                                const availableChildren = maxChildrenForThisRoom - roomsConfig[activeRoomTab].children.length;
                                
                                return availableAdults === 0 && availableChildren === 0 ? (
                                  <p className="text-[13px] text-emerald-400/90 flex items-center gap-2">
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                <span className="font-medium">
                  {guests.rooms > 1 
                    ? "All guests assigned across rooms" 
                    : "All guests assigned to this room"}
                </span>
                                  </p>
                                ) : (
                                  <p className="text-[13px] text-white/60 flex items-center gap-2">
                                    <svg className="w-3.5 h-3.5 text-[#EAC177]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                      Available to assign: 
                                      <span className="text-white/90 font-medium ml-1">
                                        {availableAdults} {availableAdults === 1 ? 'adult' : 'adults'}
                                      </span>
                                      {availableChildren > 0 && (
                                        <span className="text-white/90 font-medium ml-2">
                                          • {availableChildren} {availableChildren === 1 ? 'child' : 'children'}
                                        </span>
                                      )}
                                    </span>
                                  </p>
                                );
                              })()}
                            </div>
                            
                            <div className="space-y-4">
                              {/* Adults */}
                              <div className="flex items-center justify-between pb-3 border-b border-white/[0.08]">
                                <span className="text-[15px] text-white/92 font-medium">Adults</span>
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => {
                                      // Use computed roomsConfig (which derives from guests.roomsConfig)
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'adults', Math.max(1, currentRoom.adults - 1));
                                    }}
                                    disabled={roomsConfig[activeRoomTab].adults <= 1}
                                    className="h-8 w-8 rounded-full border border-[rgba(234,193,119,0.35)] flex items-center justify-center hover:bg-[rgba(234,193,119,0.08)] hover:border-[#EAC177] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <Minus className="w-3.5 h-3.5 text-white/90" />
                                  </button>
                                  <span className="text-[16px] text-white font-semibold min-w-[2rem] text-center">{roomsConfig[activeRoomTab].adults}</span>
                                  <button
                                    onClick={() => {
                                      // Use computed roomsConfig (which derives from guests.roomsConfig)
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'adults', currentRoom.adults + 1);
                                    }}
                                    disabled={roomsConfig[activeRoomTab].adults >= maxAdultsForThisRoom || roomsConfig[activeRoomTab].adults >= 6}
                                    className="h-8 w-8 rounded-full border border-[rgba(234,193,119,0.35)] flex items-center justify-center hover:bg-[rgba(234,193,119,0.08)] hover:border-[#EAC177] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <Plus className="w-3.5 h-3.5 text-white/90" />
                                  </button>
                                </div>
                              </div>

                              {/* Children */}
                              <div className="flex items-center justify-between">
                                <span className="text-[15px] text-white/92 font-medium">Children</span>
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => {
                                      // Use computed roomsConfig (which derives from guests.roomsConfig)
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'children', Math.max(0, currentRoom.children.length - 1));
                                    }}
                                    disabled={roomsConfig[activeRoomTab].children.length <= 0}
                                    className="h-8 w-8 rounded-full border border-[rgba(234,193,119,0.35)] flex items-center justify-center hover:bg-[rgba(234,193,119,0.08)] hover:border-[#EAC177] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <Minus className="w-3.5 h-3.5 text-white/90" />
                                  </button>
                                  <span className="text-[16px] text-white font-semibold min-w-[2rem] text-center">{roomsConfig[activeRoomTab].children.length}</span>
                                  <button
                                    onClick={() => {
                                      // Use computed roomsConfig (which derives from guests.roomsConfig)
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'children', currentRoom.children.length + 1);
                                    }}
                                    disabled={roomsConfig[activeRoomTab].children.length >= maxChildrenForThisRoom || roomsConfig[activeRoomTab].children.length >= 4}
                                    className="h-8 w-8 rounded-full border border-[rgba(234,193,119,0.35)] flex items-center justify-center hover:bg-[rgba(234,193,119,0.08)] hover:border-[#EAC177] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <Plus className="w-3.5 h-3.5 text-white/90" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })()}
                    </AnimatePresence>
                  </motion.div>
                </CollapsibleContent>
                    </>
                  );
                })()}
              </div>
            </Collapsible>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auto-save indicator */}
      <div className="flex items-center justify-center gap-2 text-xs text-white/40 mt-4">
        <Check className="w-3 h-3 text-emerald-400" />
        <span>Changes saved automatically</span>
      </div>
    </motion.div>
  );
};
